import { createRouter, type RouterHistory } from '@tanstack/react-router'
import { QueryClient } from '@tanstack/react-query'
import { routeTree } from './routeTree'
import { analytics } from '@/lib/analytics'
import { AnalyticsEvents } from '@/lib/analytics/events'

export interface RouterContext {
  queryClient: QueryClient
  isAuthenticated: boolean
}

/** `history` is injectable for tests (memory history); the app passes none and gets the browser's. */
export function createAppRouter(queryClient: QueryClient, options?: { history?: RouterHistory }) {
  const router = createRouter({
    routeTree,
    history: options?.history,
    context: { queryClient, isAuthenticated: false },
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    scrollRestorationBehavior: 'instant',
  })

  // Track page views on route changes. The URL is sent as-is: an id-bearing
  // Digest path (`/dashboard/issues/116`) is templated in every PostHog event's
  // properties (strings AND keys, e.g. the heatmap URL key) by the `before_send`
  // hook in `lib/analytics` (posthog-js adds URLs of its own to other events, so
  // per-call templating here could not cover them). It does NOT reach Sentry's
  // raw transaction URL or PostHog session-replay snapshots; see `scrubIssueIds`.
  router.subscribe('onResolved', ({ toLocation }) => {
    analytics.capture(AnalyticsEvents.PAGE_VIEW, {
      $current_url: window.location.origin + toLocation.href,
      path: toLocation.pathname,
      search: toLocation.search,
      hash: toLocation.hash,
    })
  })

  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
